import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProductTour from "../components/ProductTour";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserCog, Bell, Save, Ruler, Clock, Mail, KeyRound, Upload, Trash2, User as UserIcon, Share2, Copy, Globe, AtSign, Link as LinkIcon, MessageSquare, Lock, Facebook, Instagram, Youtube, Linkedin, Music2, Twitch, Sun, Moon, Monitor } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "../context/SettingsContext";
import { useTheme } from "../context/ThemeContext";
import StreakChip from "../components/StreakChip";
import ProfileHeader from "../components/ProfileHeader";
import MacroGoalEditor from "../components/MacroGoalEditor";
import RecapStreakBadge from "../components/RecapStreakBadge";
import SupplementStreakBadge from "../components/SupplementStreakBadge";
import ShareProgressCard from "../components/ShareProgressCard";
import SubmitTestimonialCard from "../components/SubmitTestimonialCard";
import DeleteAccountCard from "../components/DeleteAccountCard";
import PageHero from "../components/PageHero";

const COMMON_TZ = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Stockholm",
  "Europe/Moscow",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Australia/Perth",
  "Pacific/Auckland",
];

function tzList() {
  try {
    // Available in modern browsers (Node > 18, Chrome > 111)
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch { /* fall through */ }
  return COMMON_TZ;
}

const PROFILE_TOUR_STORAGE_KEY = "profile.tour.completed.v1";
const PROFILE_TOUR_STEPS = [
  {
    placement: "center",
    title: "Welcome to Your Profile",
    body: "Quick 90-second spin through everything on this page. Use ← → to navigate, Esc to skip.",
  },
  {
    selector: '[data-testid="profile-header"]',
    placement: "bottom",
    title: "Your Public Card",
    body: "Your avatar, level, follower counts, and pinned shot live here — this is what strangers see when they visit /u/:handle.",
  },
  {
    selector: '[data-testid="handle-claim-row"]',
    placement: "top",
    title: "Claim Your @Handle",
    body: "Pick a short username. Your shareable link becomes /u/yourhandle instead of a long id — cleaner for sharing on X or Reddit.",
  },
  {
    selector: '[data-testid="bio-editor"]',
    placement: "top",
    title: "Public Bio + Link",
    body: "Up to 400 characters, plus one link (your Substack, IG, wherever). Both render right under your name on your public profile.",
  },
  {
    selector: '[data-testid="privacy-toggle-row"]',
    placement: "top",
    title: "Go Private",
    body: "Flip this on and only you + site staff can see your profile. Strangers get a locked stub. Flip off any time.",
  },
  {
    selector: '[data-testid="social-links-editor"]',
    placement: "top",
    title: "Add Social Icons",
    body: "X, Facebook, Instagram, YouTube, TikTok, LinkedIn, Twitch, Discord, Rumble, or your own website — icons render on your public profile.",
  },
  {
    selector: '[data-testid="profile-identity"]',
    placement: "top",
    title: "Identity + Weight + Goals",
    body: "Your display name, avatar, body weight, goal weight, and height. Height + weight drive the dose calculator so it's worth keeping current.",
  },
  {
    selector: '[data-testid="profile-preferences"]',
    placement: "top",
    title: "Defaults + Notifications",
    body: "Your default dose interval (used by the next-dose countdown), timezone, and whether to receive email notifications.",
  },
  {
    selector: '[data-testid="profile-save"]',
    placement: "top",
    title: "Save Changes",
    body: "Hit save whenever you tweak identity or preferences. Bio, handle, privacy, and socials each have their own save button so you can update them independently.",
  },
];

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { settings } = useSettings();
  const [form, setForm] = useState({
    name: "",
    body_weight: "",
    weight_unit: "lb",
    height: "",
    height_unit: "in",
    default_interval_hours: "24",
    timezone: "",
    notifications_enabled: false,
    auto_post_weekly_recap: false,
  });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zones] = useState(tzList());
  const [tourOpen, setTourOpen] = useState(false);
  const tourLaunchedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Open the tour when arriving with ?tour=1 (from the global "?" help button)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tour") === "1") {
      setTourOpen(true);
      params.delete("tour");
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // First-run auto-launch
  useEffect(() => {
    if (tourLaunchedRef.current) return;
    try {
      if (!localStorage.getItem(PROFILE_TOUR_STORAGE_KEY)) {
        tourLaunchedRef.current = true;
        const t = setTimeout(() => setTourOpen(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);

  const closeTour = useCallback(() => {
    setTourOpen(false);
    try { localStorage.setItem(PROFILE_TOUR_STORAGE_KEY, "1"); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      body_weight: user.body_weight != null ? String(user.body_weight) : "",
      weight_unit: user.weight_unit || "lb",
      goal_weight: user.goal_weight != null ? String(user.goal_weight) : "",
      height: user.height != null ? String(user.height) : "",
      height_unit: user.height_unit || "in",
      default_interval_hours: user.default_interval_hours != null ? String(user.default_interval_hours) : "24",
      timezone: user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      notifications_enabled: !!user.notifications_enabled,
      auto_post_weekly_recap: !!user.auto_post_weekly_recap,
    });
    setAvatarUrl(user.avatar_url || null);
  }, [user]);

  const persistAvatar = async (nextUrl) => {
    setSavingAvatar(true);
    try {
      await api.patch("/users/me", { avatar_url: nextUrl });
      await refreshUser();
      toast.success(nextUrl ? "Avatar updated" : "Avatar removed");
    } catch (err) {
      toast.error(formatApiError(err));
      // Revert on failure
      setAvatarUrl(user?.avatar_url || null);
    } finally {
      setSavingAvatar(false);
    }
  };

  const onAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error("Avatar must be under 512 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setAvatarUrl(dataUrl);
      persistAvatar(dataUrl);
    };
    reader.readAsDataURL(file);
    // Reset the input so re-uploading the same file re-fires
    e.target.value = "";
  };

  const onAvatarRemove = () => {
    setAvatarUrl(null);
    persistAvatar(null);
  };

  const handleNotificationsToggle = async (v) => {
    if (v && "Notification" in window) {
      const perm = Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Enable browser notifications to receive dose reminders.");
        setForm({ ...form, notifications_enabled: false });
        return;
      }
    }
    setForm({ ...form, notifications_enabled: v });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim() || null,
        body_weight: form.body_weight === "" ? null : parseFloat(form.body_weight),
        weight_unit: form.weight_unit,
        goal_weight: form.goal_weight === "" ? null : parseFloat(form.goal_weight),
        height: form.height === "" ? null : parseFloat(form.height),
        height_unit: form.height_unit,
        default_interval_hours: form.default_interval_hours === "" ? 24 : parseFloat(form.default_interval_hours),
        timezone: form.timezone || null,
        notifications_enabled: form.notifications_enabled,
        auto_post_weekly_recap: !!form.auto_post_weekly_recap,
      };
      await api.patch("/users/me", payload);
      await refreshUser();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <PageHero
        eyebrow="Account"
        title="Profile"
        subtitle="Manage your personal preferences and defaults."
        icon={<UserCog className="w-8 h-8 text-[#0D9488]" />}
        gradient
      />

      {/* Trial Countdown pill — visible when user has_premium AND an expiry set */}
      {user?.has_premium && user?.premium_expires_at && <TrialCountdownPill expiresAt={user.premium_expires_at} />}

      {/* Skool-style profile header — banner + avatar + level + stats + activity */}
      {user?.id && <ProfileHeader userId={user.id} />}

      {/* Share-your-profile row (only when the admin has enabled public profiles) */}
      {user?.id && settings?.public_profiles_enabled && <ShareProfileRow userId={user.id} handle={user.handle} />}

      {/* Handle (public @username) — shown regardless of the admin toggle so folks can claim early */}
      {user?.id && <HandleClaimRow />}

      {/* Public bio + link — shown to anyone visiting your /u/:handle */}
      {user?.id && <BioEditor />}

      {/* Privacy toggle — flip on to hide your profile from strangers (still visible to staff) */}
      {user?.id && <PrivacyToggleRow />}

      {/* Social links editor — the icons you'd expect (X, Facebook, Instagram, etc.) */}
      {user?.id && <SocialLinksEditor />}

      {/* Streak chip — the full heatmap now lives inside the ProfileHeader banner */}
      <div className="mb-6">
        <StreakChip />
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="profile-form">
        {/* Identity */}
        <Card className="lg:col-span-2 bg-white border border-slate-200 rounded-none p-6" data-testid="profile-identity">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-4">// Identity</div>
          <div className="space-y-4">
            {/* Avatar */}
            <div className="space-y-1.5" data-testid="profile-avatar-block">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                <UserIcon className="w-3 h-3" /> Community Avatar
              </Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar-preview" />
                  ) : (
                    <span className="text-slate-500 font-display font-black text-xl" data-testid="profile-avatar-placeholder">
                      {(form.name || user?.email || "?").trim()[0]?.toUpperCase() || "?"}
                    </span>
                  )}
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="inline-flex items-center gap-2 border border-slate-200 hover:border-[#0D9488] bg-slate-50 hover:bg-slate-50 px-3 h-9 cursor-pointer transition-colors text-xs font-mono-num uppercase tracking-widest text-slate-800">
                    <Upload className="w-3.5 h-3.5" /> {savingAvatar ? "Uploading…" : "Upload image (≤ 512 KB)"}
                    <input type="file" accept="image/*" onChange={onAvatarUpload} disabled={savingAvatar} className="hidden" data-testid="profile-avatar-input" />
                  </label>
                  {avatarUrl && (
                    <Button type="button" variant="outline" onClick={onAvatarRemove} disabled={savingAvatar}
                      className="h-8 border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-600 text-xs uppercase tracking-widest font-mono-num"
                      data-testid="profile-avatar-remove">
                      <Trash2 className="w-3 h-3 mr-1.5" /> Remove
                    </Button>
                  )}
                  <div className="text-[10px] text-slate-500 font-mono-num uppercase tracking-widest">
                    Shown next to your posts + replies in the community.
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Display Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="profile-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Email</Label>
                <Input value={user?.email || ""} readOnly disabled
                  className="bg-slate-50 border-slate-200 rounded-none h-11 text-slate-500" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Body Weight</Label>
                <Input type="number" step="any" value={form.body_weight}
                  onChange={(e) => setForm({ ...form, body_weight: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="profile-weight" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Unit</Label>
                <Select value={form.weight_unit} onValueChange={(v) => setForm({ ...form, weight_unit: v })}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    <SelectItem value="lb">lb</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Goal Weight ({form.weight_unit})</Label>
              <Input type="number" step="any" value={form.goal_weight}
                onChange={(e) => setForm({ ...form, goal_weight: e.target.value })}
                placeholder="Target weight"
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="profile-goal-weight" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1">
                  <Ruler className="w-3 h-3" /> Height
                </Label>
                <Input type="number" step="any" value={form.height}
                  onChange={(e) => setForm({ ...form, height: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="profile-height" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Unit</Label>
                <Select value={form.height_unit} onValueChange={(v) => setForm({ ...form, height_unit: v })}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    <SelectItem value="in">in</SelectItem>
                    <SelectItem value="cm">cm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </Card>

        {/* Preferences */}
        <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="profile-preferences">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-4">// Preferences</div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1">
                <Clock className="w-3 h-3" /> Default Interval (hours)
              </Label>
              <Input type="number" step="0.5" value={form.default_interval_hours}
                onChange={(e) => setForm({ ...form, default_interval_hours: e.target.value })}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="profile-interval" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Timezone</Label>
              <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="profile-timezone">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none max-h-[300px]">
                  {zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 border border-slate-200 bg-slate-50">
              <div className="flex items-start gap-2">
                <Bell className="w-4 h-4 text-[#0D9488] mt-0.5" />
                <div>
                  <div className="text-sm text-slate-900 font-medium">Dose Reminders</div>
                  <div className="text-[11px] text-slate-500">Notify me when next dose is due</div>
                </div>
              </div>
              <Switch checked={form.notifications_enabled} onCheckedChange={handleNotificationsToggle}
                className="data-[state=checked]:bg-[#0D9488]" data-testid="profile-notifications" />
            </div>

            {/* Auto-post weekly recap to Community */}
            <div className="flex items-start justify-between gap-4 py-2 border-t border-slate-200">
              <div className="flex items-start gap-3">
                <Share2 className="w-4 h-4 text-teal-400 mt-0.5" />
                <div>
                  <div className="text-sm text-slate-900 font-medium">Auto-post Weekly Recap</div>
                  <div className="text-[11px] text-slate-500">
                    Every Sunday, share my week to Community automatically (alongside the digest email)
                  </div>
                </div>
              </div>
              <Switch
                checked={!!form.auto_post_weekly_recap}
                onCheckedChange={(v) => setForm({ ...form, auto_post_weekly_recap: v })}
                className="data-[state=checked]:bg-teal-500"
                data-testid="profile-auto-post-recap"
              />
            </div>

            <TimezonePreferenceRow user={user} />
            <ThemePreferenceRow />
          </div>
        </Card>

        <div className="lg:col-span-3">
          <RecapStreakBadge />
        </div>

        <div className="lg:col-span-3">
          <SupplementStreakBadge />
        </div>

        <div className="lg:col-span-3">
          <MacroGoalEditor />
        </div>

        <div className="lg:col-span-3 flex justify-end">
          <Button type="submit" disabled={saving}
            className="h-12 px-8 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
            data-testid="profile-save">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Profile"}
          </Button>
        </div>
      </form>

      <SecuritySection />

      {/* Public "Submit a Testimonial" form — visible to every signed-in user. */}
      <div className="mt-8">
        <SubmitTestimonialCard />
      </div>

      {/* Share Progress — grant a coach view-only access */}
      <div className="mt-8">
        <ShareProgressCard />
      </div>

      <DeleteAccountCard />

      <ProductTour steps={PROFILE_TOUR_STEPS} open={tourOpen} onClose={closeTour} />
    </AppLayout>
  );
}

function ShareProfileRow({ userId, handle }) {
  const key = handle || userId;
  const url = `${window.location.origin}/u/${key}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public profile URL copied");
    } catch {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };
  return (
    <div className="mb-6 border border-[color:var(--brand,#0D9488)]/30 bg-[color:var(--brand,#0D9488)]/[0.04] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="share-profile-row">
      <div className="flex items-center gap-2 shrink-0">
        <Globe className="w-4 h-4 text-[color:var(--brand,#0D9488)]" />
        <span className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[color:var(--brand,#0D9488)]">// Shareable link</span>
      </div>
      <code className="font-mono-num text-xs text-slate-900 truncate flex-1" data-testid="share-profile-url">{url}</code>
      <div className="flex items-center gap-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-profile-open"
          className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-[color:var(--brand,#0D9488)] px-3 h-8 text-[11px] uppercase tracking-widest font-mono-num text-slate-800 hover:text-[color:var(--brand,#0D9488)] transition-colors"
        >
          <Share2 className="w-3 h-3" /> Open
        </a>
        <button
          type="button"
          onClick={copy}
          data-testid="share-profile-copy"
          className="inline-flex items-center gap-1.5 bg-[color:var(--brand,#0D9488)] hover:opacity-90 text-black px-3 h-8 text-[11px] uppercase tracking-widest font-semibold"
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
    </div>
  );
}

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const RumbleLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M12 2 L22 12 L12 22 L2 12 Z M9 8 L9 16 L11 16 L11 13 L13 13 L15 16 L17 16 L15 12.5 A2 2 0 0 0 13 8 Z M11 10 L13 10 A0.6 0.6 0 0 1 13 11.4 L11 11.4 Z" />
  </svg>
);

const DiscordLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.443.864-.607 1.25a18.27 18.27 0 0 0-5.486 0 12.6 12.6 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.68 4.37a.07.07 0 0 0-.032.027C.533 9.045-.32 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.042-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.927 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.699.772 1.364 1.226 1.994a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.947 2.42-2.157 2.42z" />
  </svg>
);

const SOCIAL_PLATFORMS = [
  { key: "x",         label: "X",         icon: XLogo,     placeholder: "https://x.com/yourhandle" },
  { key: "facebook",  label: "Facebook",  icon: Facebook,  placeholder: "https://facebook.com/you" },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/you" },
  { key: "youtube",   label: "YouTube",   icon: Youtube,   placeholder: "https://youtube.com/@you" },
  { key: "tiktok",    label: "TikTok",    icon: Music2,    placeholder: "https://tiktok.com/@you" },
  { key: "linkedin",  label: "LinkedIn",  icon: Linkedin,  placeholder: "https://linkedin.com/in/you" },
  { key: "twitch",    label: "Twitch",    icon: Twitch,    placeholder: "https://twitch.tv/you" },
  { key: "discord",   label: "Discord",   icon: DiscordLogo, placeholder: "https://discord.gg/yourinvite" },
  { key: "rumble",    label: "Rumble",    icon: RumbleLogo, placeholder: "https://rumble.com/user/you" },
  { key: "website",   label: "Website",   icon: Globe,     placeholder: "https://yoursite.com" },
];

function SocialLinksEditor() {
  const { user, refreshUser } = useAuth();
  const [links, setLinks] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLinks(user?.social_links || {}); }, [user?.social_links]);

  const submit = async (e) => {
    e.preventDefault();
    // Normalize: trim, add https://, drop empties, validate URLs
    const cleaned = {};
    for (const p of SOCIAL_PLATFORMS) {
      const raw = (links[p.key] || "").trim();
      if (!raw) continue;
      const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      try { new URL(withScheme); } catch { toast.error(`${p.label} link isn't a valid URL`); return; }
      cleaned[p.key] = withScheme;
    }
    setSaving(true);
    try {
      await api.patch("/users/me", { social_links: cleaned });
      await refreshUser?.();
      toast.success("Social links saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mb-6 border border-slate-200 bg-white p-4 space-y-3" data-testid="social-links-editor">
      <div className="flex items-center gap-2">
        <Share2 className="w-4 h-4 text-[color:var(--brand,#0D9488)]" />
        <span className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Social links</span>
        <span className="ml-auto font-mono-num text-[10px] uppercase tracking-widest text-slate-400">Shown on your public profile</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {SOCIAL_PLATFORMS.map(({ key, label, icon: Icon, placeholder }) => (
          <div key={key} className="flex items-center gap-1 bg-slate-50 border border-slate-200 h-9 px-2 focus-within:border-[color:var(--brand,#0D9488)]">
            <Icon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <span className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest w-16 shrink-0">{label}</span>
            <input
              type="text"
              value={links[key] || ""}
              onChange={(e) => setLinks((cur) => ({ ...cur, [key]: e.target.value }))}
              placeholder={placeholder}
              maxLength={200}
              className="bg-transparent outline-none flex-1 min-w-0 text-xs font-mono-num text-slate-900 placeholder-zinc-700"
              data-testid={`social-input-${key}`}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 bg-[color:var(--brand,#0D9488)] hover:opacity-90 text-black px-4 h-9 text-[11px] uppercase tracking-widest font-semibold disabled:opacity-50"
          data-testid="social-links-save"
        >
          <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save Links"}
        </button>
      </div>
    </form>
  );
}


/**
 * Light / Dark / System radio group so users can choose their theme from the
 * Preferences card in addition to the header icon. Reads/writes through the
 * shared ThemeContext so the header + Profile stay in sync in real time.
 */
function ThemePreferenceRow() {
  const { pref, resolved, setTheme } = useTheme();
  const OPTIONS = [
    { value: "light",  label: "Light",  Icon: Sun,     desc: "Bright surfaces, best in daylight" },
    { value: "dark",   label: "Dark",   Icon: Moon,    desc: "Deep slate + brighter teal accents" },
    { value: "system", label: "System", Icon: Monitor, desc: `Follow your device (currently ${resolved})` },
  ];
  return (
    <div className="py-2 border-t border-slate-200" data-testid="profile-theme-row">
      <div className="flex items-start gap-3 mb-3">
        <Sun className="w-4 h-4 text-teal-500 mt-0.5" />
        <div>
          <div className="text-sm text-slate-900 font-medium">Appearance</div>
          <div className="text-[11px] text-slate-500">Pick a theme or let it follow your device</div>
        </div>
      </div>
      <div role="radiogroup" aria-label="Theme preference" className="grid grid-cols-3 gap-2" data-testid="profile-theme-radios">
        {OPTIONS.map(({ value, label, Icon, desc }) => {
          const active = pref === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setTheme(value)}
              title={desc}
              data-testid={`profile-theme-${value}`}
              className={`flex flex-col items-start gap-1.5 p-3 border-2 rounded-lg text-left transition-colors ${
                active
                  ? "border-teal-500 bg-teal-50 text-teal-800"
                  : "border-slate-200 bg-white hover:border-teal-300 text-slate-700"
              }`}
            >
              <Icon className={`w-4 h-4 ${active ? "text-teal-600" : "text-slate-500"}`} />
              <div className="text-sm font-semibold">{label}</div>
              <div className={`text-[10px] leading-snug ${active ? "text-teal-700" : "text-slate-500"}`}>{desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function TimezonePreferenceRow({ user }) {
  const { refreshUser } = useAuth();
  const { settings } = useSettings();
  const [enabled, setEnabled] = useState(!!user?.use_local_timezone);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setEnabled(!!user?.use_local_timezone); }, [user?.use_local_timezone]);

  const siteTz = settings?.site_timezone || "America/Chicago";
  let browserTz = "";
  try { browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* ignore */ }

  const toggle = async (next) => {
    setBusy(true);
    const prev = enabled;
    setEnabled(next);
    try {
      await api.patch("/users/me", { use_local_timezone: next });
      await refreshUser?.();
      toast.success(next
        ? `Times will show in your local zone (${browserTz})`
        : `Times will show in ${siteTz}`);
    } catch (e) {
      setEnabled(prev);
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center justify-between p-3 border border-slate-200 bg-slate-50" data-testid="profile-timezone-row">
      <div className="flex items-start gap-2">
        <Clock className="w-4 h-4 text-[#0D9488] mt-0.5" />
        <div>
          <div className="text-sm text-slate-900 font-medium">Show times in my local zone</div>
          <div className="text-[11px] text-slate-500">
            Off = <span className="text-slate-800">{siteTz}</span> · On = <span className="text-slate-800">{browserTz || "your browser zone"}</span>
          </div>
        </div>
      </div>
      <Switch
        checked={enabled}
        disabled={busy}
        onCheckedChange={toggle}
        className="data-[state=checked]:bg-[#0D9488]"
        data-testid="profile-timezone-toggle"
      />
    </div>
  );
}



function PrivacyToggleRow() {
  const { user, refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setEnabled(!!user?.profile_private); }, [user?.profile_private]);

  const toggle = async () => {
    const next = !enabled;
    setBusy(true);
    setEnabled(next);
    try {
      await api.patch("/users/me", { profile_private: next });
      await refreshUser?.();
      toast.success(next ? "Profile is now private" : "Profile is public again");
    } catch (e) {
      setEnabled(!next);
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-6 border border-slate-200 bg-white px-4 py-3 flex items-center gap-3" data-testid="privacy-toggle-row">
      <Lock className="w-4 h-4 text-[color:var(--brand,#0D9488)] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-black uppercase tracking-tight text-sm text-slate-900">Private profile</div>
        <div className="text-[11px] font-mono-num text-slate-500 uppercase tracking-widest">
          {enabled ? "Only you and site staff can see your profile" : "Anyone signed in can view your profile"}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={busy}
        onClick={toggle}
        data-testid="privacy-toggle"
        className={`relative inline-flex h-6 w-11 items-center transition-colors ${enabled ? "bg-[color:var(--brand,#0D9488)]" : "bg-zinc-800"} disabled:opacity-50`}
      >
        <span className={`inline-block h-4 w-4 transform bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
      </button>
    </div>
  );
}


function BioEditor() {
  const { user, refreshUser } = useAuth();
  const [bio, setBio] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBio(user?.bio || "");
    setLinkUrl(user?.link_url || "");
  }, [user?.bio, user?.link_url]);

  const submit = async (e) => {
    e.preventDefault();
    if ((bio || "").length > 400) { toast.error("Bio must be 400 characters or less"); return; }
    const rawUrl = (linkUrl || "").trim();
    let finalUrl = null;
    if (rawUrl) {
      const withScheme = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
      try { new URL(withScheme); finalUrl = withScheme; } catch { toast.error("Link doesn't look like a valid URL"); return; }
    }
    setSaving(true);
    try {
      await api.patch("/users/me", { bio: (bio || "").trim() || null, link_url: finalUrl });
      await refreshUser?.();
      toast.success("Public bio saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mb-6 border border-slate-200 bg-white p-4 space-y-3" data-testid="bio-editor">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-[color:var(--brand,#0D9488)]" />
        <span className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Public bio &amp; link</span>
        <span className="ml-auto font-mono-num text-[10px] uppercase tracking-widest text-slate-400 tabular-nums">{(bio || "").length}/400</span>
      </div>
      <textarea
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        placeholder="Tell people about your protocol, goals, or wins. Rendered under your name on /u/:handle."
        rows={3}
        maxLength={400}
        className="w-full bg-slate-50 border border-slate-200 rounded-none px-3 py-2 text-sm text-slate-900 placeholder-zinc-600 outline-none focus:border-[color:var(--brand,#0D9488)] resize-none"
        data-testid="bio-textarea"
      />
      <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 h-9 px-2">
        <LinkIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        <input
          type="text"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="Optional link (Instagram, Substack, your Skool, etc.)"
          maxLength={200}
          className="bg-transparent outline-none flex-1 text-sm font-mono-num text-slate-900 placeholder-zinc-600"
          data-testid="bio-link-input"
        />
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 bg-[color:var(--brand,#0D9488)] hover:opacity-90 text-black px-4 h-9 text-[11px] uppercase tracking-widest font-semibold disabled:opacity-50"
          data-testid="bio-save"
        >
          <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save Bio"}
        </button>
      </div>
    </form>
  );
}


function HandleClaimRow() {
  const { user, refreshUser } = useAuth();
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(user?.handle || ""); }, [user?.handle]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = (value || "").trim().toLowerCase().replace(/^@/, "");
      const { data } = await api.patch("/users/me/handle", { handle: clean || null });
      await refreshUser?.();
      toast.success(data.handle ? `Claimed @${data.handle}` : "Handle cleared");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 border border-slate-200 bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="handle-claim-row">
      <div className="flex items-center gap-2 shrink-0">
        <AtSign className="w-4 h-4 text-[color:var(--brand,#0D9488)]" />
        <span className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Handle</span>
      </div>
      <div className="flex items-center gap-1 flex-1 bg-slate-50 border border-slate-200 h-9 px-2">
        <span className="text-slate-500 font-mono-num text-sm">/u/</span>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="pick a handle (a-z, 0-9, _)"
          maxLength={20}
          pattern="[a-z0-9_]{3,20}"
          className="bg-transparent outline-none flex-1 text-sm font-mono-num text-slate-900 placeholder-zinc-600"
          data-testid="handle-input"
        />
      </div>
      <button
        type="submit"
        disabled={saving}
        className="inline-flex items-center gap-1.5 bg-[color:var(--brand,#0D9488)] hover:opacity-90 text-black px-3 h-9 text-[11px] uppercase tracking-widest font-semibold disabled:opacity-50"
        data-testid="handle-save"
      >
        <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
      </button>
      <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest sm:w-full sm:pl-6 sm:ml-6 sm:border-l sm:border-slate-200">
        3-20 lowercase letters / digits / underscore. Empty to clear.
      </div>
    </form>
  );
}

function SecuritySection() {
  const { user, refresh } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const changeEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || !emailPw) { toast.error("Fill both fields"); return; }
    setSavingEmail(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail, current_password: emailPw });
      toast.success("Email updated");
      setNewEmail(""); setEmailPw("");
      refresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingEmail(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated");
      setCurPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingPw(false); }
  };

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Email */}
      <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="profile-email-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 bg-[#0D9488] flex items-center justify-center"><Mail className="w-4 h-4 text-black" /></div>
          <div>
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Account</div>
            <div className="font-display font-black uppercase tracking-tight text-xl">Change Email</div>
          </div>
        </div>
        <div className="text-xs font-mono-num uppercase tracking-widest text-slate-500 mb-4">
          Current: <span className="text-slate-800 normal-case">{user?.email}</span>
        </div>
        <form onSubmit={changeEmail} className="space-y-3" data-testid="profile-email-form">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">New Email</Label>
            <Input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="profile-new-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Current Password</Label>
            <Input type="password" required value={emailPw} onChange={(e) => setEmailPw(e.target.value)}
              className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="profile-email-current-password" />
          </div>
          <div className="pt-1 flex justify-end">
            <Button type="submit" disabled={savingEmail}
              className="h-10 px-5 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest text-xs font-semibold"
              data-testid="profile-email-submit">
              <Mail className="w-3.5 h-3.5 mr-2" /> {savingEmail ? "Updating…" : "Update email"}
            </Button>
          </div>
        </form>
      </Card>

      {/* Password */}
      <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="profile-password-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 bg-[#0D9488] flex items-center justify-center"><KeyRound className="w-4 h-4 text-black" /></div>
          <div>
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Security</div>
            <div className="font-display font-black uppercase tracking-tight text-xl">Change Password</div>
          </div>
        </div>
        <form onSubmit={changePw} className="space-y-3" data-testid="profile-password-form">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Current Password</Label>
            <Input type="password" required value={curPw} onChange={(e) => setCurPw(e.target.value)}
              className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="profile-current-password" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">New Password (≥ 8 chars)</Label>
            <Input type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)}
              className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="profile-new-password" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Confirm New Password</Label>
            <Input type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
              className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="profile-confirm-password" />
          </div>
          <div className="pt-1 flex justify-end">
            <Button type="submit" disabled={savingPw}
              className="h-10 px-5 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest text-xs font-semibold"
              data-testid="profile-password-submit">
              <KeyRound className="w-3.5 h-3.5 mr-2" /> {savingPw ? "Updating…" : "Update password"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function TrialCountdownPill({ expiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const target = new Date(expiresAt).getTime();
  const remaining = target - now;
  if (remaining <= 0) return null;
  const days = Math.floor(remaining / 86_400_000);
  const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
  const mins = Math.floor((remaining % 3_600_000) / 60_000);
  const urgent = remaining < 3 * 86_400_000; // last 3 days
  const label = days > 0 ? `${days}d ${hours}h left` : hours > 0 ? `${hours}h ${mins}m left` : `${mins}m left`;

  return (
    <div className={`mt-4 mb-2 rounded-xl border-l-4 p-4 flex items-center gap-3 ${
      urgent ? "bg-amber-50 border-l-amber-500" : "bg-teal-50 border-l-teal-500"
    }`}
         role="status" data-testid="profile-trial-pill">
      <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
        urgent ? "bg-amber-100 text-amber-700" : "bg-teal-100 text-teal-700"
      }`}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] uppercase tracking-widest font-semibold ${urgent ? "text-amber-700" : "text-teal-700"}`}>
          Pro trial · comped access
        </div>
        <div className="text-sm text-slate-800 mt-0.5">
          You have <b className="tabular-nums font-mono-num" data-testid="profile-trial-remaining">{label}</b> of Pro access before you're auto-reverted to the Free plan.
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          Ends {new Date(expiresAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

